import api from "../services/authService";

/**
 * The plan of record — the attorney's standing strategy for a matter, as
 * data (backend dms_backend #2577; design in whg-salesforce
 * docs/big-lawyer-build.md).
 *
 * The knowledge graph holds the facts of the file; the strategy memo holds
 * the attorney's decisions in prose. The plan holds those decisions as
 * data — per issue the goal, the lane in play, the current step, the
 * if-granted / if-denied / if-partial branches, and the filing we are
 * waiting on — so Context+ can read every arriving letter against them.
 *
 * Versioned per matter. The memo engine proposes DRAFTS; only an attorney
 * may approve one, and the newest approved version is the plan of record.
 */

/**
 * GET /api/matters/<matter_id>/case-plan/
 *
 * `{ plan, loops, versions }` — `plan` is the newest approved version,
 * else the newest draft, else `null` (no plan on this matter yet).
 * `loops` are the open loops rendered to the model (`L1`, `L2`, …):
 * declared loops plus one derived from each active issue's
 * `waiting_on`. `versions` is the full history, newest first.
 */
export const getMatterCasePlan = (matterId) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.get(`/matters/${matterId}/case-plan/`);
};

/**
 * POST /api/matters/<matter_id>/case-plan/approve/
 *
 * Make a draft the plan of record. `version` pins the draft being
 * approved so a draft that landed while the attorney was reading is
 * never approved by accident; omitted, the newest draft is approved.
 * Refused (403) for fenced service accounts — Hermes may propose, never
 * decide. 404 when nothing is in draft; 409 when that version was
 * superseded by a newer draft.
 */
export const approveMatterCasePlan = (matterId, { version } = {}) => {
  if (matterId == null) return Promise.reject(new Error("matterId required"));
  return api.post(`/matters/${matterId}/case-plan/approve/`, {
    ...(version != null ? { version } : {}),
  });
};
